import React from 'react';
import styled from 'styled-components';
import AuthPanel from './AuthPanel';

const Container = styled.div`
  min-height: 100vh;
  padding: 20px;
  position: relative;
  z-index: 1;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  padding: 8px 16px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

const Content = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding-bottom: 80px;
`;

const Section = styled.section`
  margin-bottom: 64px;
  padding-top: 40px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.85rem, 4.5vw, 2.35rem);
  font-weight: 800;
  margin: 0 0 14px;
  color: #fff;
  text-align: center;
  letter-spacing: -0.02em;
  line-height: 1.15;
`;

const SectionHeader = styled.div`
  margin-bottom: 0;
`;

const SectionSubtitle = styled.p`
  text-align: center;
  color: #00f5ff;
  font-size: clamp(1.15rem, 2.8vw, 1.35rem);
  font-weight: 600;
  margin: 0;
  letter-spacing: 0.01em;
  line-height: 1.35;
  text-shadow: 0 0 24px rgba(0, 245, 255, 0.25);
`;

const SectionLead = styled.div`
  margin-top: 20px;
`;

/* ── Hero ── */

const Hero = styled.header`
  text-align: center;
  margin-bottom: 0;
  padding: 32px 20px 40px;
  border-radius: 28px;
  background: radial-gradient(ellipse at 50% 0%, rgba(255, 0, 170, 0.12) 0%, transparent 60%),
    radial-gradient(ellipse at 80% 60%, rgba(0, 245, 255, 0.08) 0%, transparent 50%);
  border: 1px solid rgba(255, 255, 255, 0.06);
`;

const PageTitle = styled.h1`
  font-size: clamp(2.2rem, 5vw, 3rem);
  font-weight: 800;
  margin: 0 0 16px;
  background: linear-gradient(135deg, #ff00aa, #00f5ff, #ffee00);
  background-size: 200% 200%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const HeroIntro = styled(SectionHeader)`
  max-width: 640px;
  margin: 0 auto;
`;

const HeroSubtitle = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.95);
  font-size: clamp(1.25rem, 2.8vw, 1.5rem);
  font-weight: 600;
  margin: 0 auto;
  max-width: 640px;
  line-height: 1.4;
`;

const HeroMission = styled.p`
  color: rgba(255, 255, 255, 0.82);
  font-size: clamp(1rem, 2.1vw, 1.125rem);
  font-weight: 400;
  line-height: 1.65;
  margin: 36px auto 0;
  max-width: min(920px, 100%);
  padding: 0 8px;
  text-align: center;
`;

/* ── Discovery gap ── */

const GapPanel = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 24px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(255, 0, 170, 0.06), rgba(0, 245, 255, 0.04));
    pointer-events: none;
  }
`;

const GapGrid = styled.div`
  display: grid;
  gap: 16px;
  align-items: stretch;
  position: relative;
  max-width: 760px;
  margin: 0 auto;

  @media (min-width: 640px) {
    grid-template-columns: 1fr auto 1fr;
    gap: 0;
    align-items: stretch;
  }
`;

const GapCard = styled.article`
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;
  padding: 24px 20px;
  height: 100%;
  min-height: 168px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const GapConnector = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 12px 0;

  @media (min-width: 640px) {
    padding: 0 16px;
    flex-direction: row;
    gap: 0;
    align-self: center;
  }
`;

const ConnectorArm = styled.span`
  display: none;
  font-size: 1.75rem;
  color: rgba(0, 245, 255, 0.7);
  line-height: 1;
  padding: 0 6px;

  @media (min-width: 640px) {
    display: block;
  }
`;

const HandshakeBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 76px;
  border-radius: 50%;
  font-size: 2.75rem;
  line-height: 1;
  background: rgba(0, 0, 0, 0.5);
  border: 2px solid rgba(0, 245, 255, 0.45);
  box-shadow: 0 0 28px rgba(255, 0, 170, 0.2), 0 0 24px rgba(0, 245, 255, 0.3);
`;

const GapLabel = styled.h3`
  margin: 0 0 12px;
  font-size: 1rem;
  font-weight: 700;
  color: #00f5ff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const GapText = styled.p`
  margin: 0;
  font-size: 0.94rem;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.78);
`;

const GapBridge = styled.p`
  text-align: center;
  margin: 28px 0 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #fff;
  position: relative;
  padding-top: 24px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 48px;
    height: 2px;
    background: linear-gradient(90deg, #ff00aa, #00f5ff);
    border-radius: 2px;
  }
`;

/* ── What we do cards ── */

const CardGrid = styled.div`
  display: grid;
  gap: 20px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const FeatureCard = styled.article`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 28px 22px;
  text-align: center;
  transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: rgba(0, 245, 255, 0.35);
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(255, 0, 170, 0.1);
  }
`;

const CardIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 14px;
  line-height: 1;
`;

const CardTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
`;

const CardText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.72);
`;

/* ── Story ── */

const StoryIntro = styled.p`
  text-align: left;
  font-size: 0.95rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.58);
  margin: 14px 0 36px;
  width: 100%;
`;

const AndersonHighlight = styled.span`
  color: #00f5ff;
  font-weight: 700;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 52px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const StatBlock = styled.div`
  text-align: center;
  background: linear-gradient(160deg, rgba(255, 0, 170, 0.1), rgba(0, 245, 255, 0.06));
  border: 1px solid rgba(0, 245, 255, 0.22);
  border-radius: 18px;
  padding: 28px 16px 24px;
`;

const StatValue = styled.div`
  font-size: clamp(2.4rem, 6vw, 3.25rem);
  font-weight: 800;
  background: linear-gradient(135deg, #ff00aa, #00f5ff);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 8px;
  line-height: 1;
  letter-spacing: -0.02em;
`;

const StatLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
`;

const HorizontalTimeline = styled.div`
  position: relative;
  max-width: 880px;
  margin: 0 auto;
`;

const TimelineTrack = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 10% 16px;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: 50%;
      height: 1px;
      background: rgba(255, 255, 255, 0.18);
      transform: translateY(-50%);
    }
  }
`;

const TimelineMarker = styled.span`
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => props.$accent || '#00f5ff'};
  box-shadow: 0 0 8px ${(props) => props.$accent || 'rgba(0, 245, 255, 0.5)'};
  position: relative;
  z-index: 1;
  flex-shrink: 0;
`;

const TimelineCards = styled.div`
  display: grid;
  gap: 14px;
  text-align: center;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    align-items: start;
  }
`;

const MilestoneCard = styled.article`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 14px;
  padding: 18px 16px;
  height: 100%;
  text-align: center;
`;

const MilestoneTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 0.9rem;
  font-weight: 700;
  color: #fff;
`;

const MilestoneText = styled.p`
  margin: 0 auto;
  font-size: 0.84rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.65);
`;

/* ── Team ── */

const TeamGrid = styled.div`
  display: grid;
  gap: 28px;
`;

const TeamCard = styled.article`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 28px;
`;

const TeamCardHeader = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 20px;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  min-width: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff00aa, #00f5ff);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  box-shadow: 0 0 24px rgba(255, 0, 170, 0.25);
  overflow: hidden;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const MemberHeaderText = styled.div`
  flex: 1;
`;

const MemberName = styled.h3`
  margin: 0 0 4px;
  font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
`;

const MemberRole = styled.p`
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #00f5ff;
`;

const MemberIntro = styled.p`
  margin: 0 0 20px;
  font-size: 0.95rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.82);
`;

const BioSections = styled.div`
  display: grid;
  gap: 16px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const BioSection = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 14px;
  padding: 16px 18px;
`;

const BioSectionTitle = styled.h4`
  margin: 0 0 10px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
`;

const BioList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const BioListItem = styled.li`
  position: relative;
  padding-left: ${(props) => (props.$indented ? '28px' : '14px')};
  margin-bottom: 8px;
  font-size: 0.88rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, ${(props) => (props.$indented ? '0.72' : '0.8')});

  &:last-child {
    margin-bottom: 0;
  }

  &::before {
    content: '•';
    position: absolute;
    left: ${(props) => (props.$indented ? '14px' : '0')};
    color: #00f5ff;
    font-weight: 700;
  }
`;

function normalizeBioItem(item) {
  if (typeof item === 'string') return { text: item, indented: false };
  return { text: item.text, indented: !!item.indented };
}

const WHAT_WE_DO = [
  {
    icon: '🔍',
    title: 'Discover',
    text: 'Find immersive experiences across VR, AR, 360°, and location-based entertainment.'
  },
  {
    icon: '📖',
    title: 'Learn',
    text: 'Understand experiences through previews, compatibility, reviews, and key details before committing.'
  },
  {
    icon: '🚀',
    title: 'Access',
    text: 'Launch experiences, download apps, buy tickets, or visit venues from one place.'
  }
];

const RESEARCH_STATS = [
  { value: '175+', label: 'Industry Interviews' },
  { value: '700+', label: 'Consumer Survey Responses' },
  { value: '150+', label: 'Product Demonstrations' }
];

const STORY_MILESTONES = [
  {
    title: 'Built on Research',
    text: 'The research consistently pointed toward the same conclusion: immersive entertainment needed a dedicated discovery platform designed for both audiences and creators.',
    accent: '#ff00aa'
  },
  {
    title: 'Evolving with the Industry',
    text: 'As the research continued, RealityCheck evolved through multiple iterations, expanding from an XR discovery platform into a home for immersive entertainment across VR, AR, 360°, and location-based experiences.',
    accent: '#00f5ff'
  },
  {
    title: 'Today',
    text: 'RealityCheck is helping shape the future of immersive entertainment by making the category easier to discover for audiences while creating greater visibility and opportunities for creators.',
    accent: '#ffee00'
  }
];

const TEAM = [
  {
    name: 'Tanvi Agrawal',
    pronouns: 'they/them',
    role: 'Founder & CEO',
    initials: 'TA',
    photo: '/team/tanvi-agrawal.png',
    intro:
      'Tanvi Agrawal is an XR producer, product manager, and immersive entertainment advocate. After growing up directing theater, they discovered immersive filmmaking in college, inspiring a career at the intersection of storytelling and emerging technology.',
    sections: [
      {
        label: '🏢 Experience',
        items: [
          'Sony Interactive Entertainment, Product Innovation Management Intern',
          'UCLA REMAP (Research Center for Engineering, Media and Performance)',
          'Ferryman Collective (award-winning interactive VR theater company)',
          'Agile Lens (leading XR development studio)'
        ]
      },
      {
        label: '🏆 Industry Leadership',
        items: [
          'Founder of BruinXR, UCLA\'s student XR club',
          'AWE Track Chair: Creator (2025), Media & Entertainment (2026)',
          'Creator of @tanvixreality, sharing immersive entertainment reviews, creator spotlights, and industry insights.',
          'Speaker at USC (navigating entrepreneurship in emerging technology)'
        ]
      },
      {
        label: '🎓 Education',
        items: [
          'MBA, UCLA Anderson School of Management',
          { text: 'Specializations in Entertainment, Technology, and Entrepreneurship', indented: true },
          'B.A., UC Berkeley',
          { text: 'Honors Thesis: Augmented Reality and Human 2.0', indented: true }
        ]
      },
      {
        label: '🎯 Focus',
        items: [
          'XR',
          'Immersive Theater',
          'AI-Powered Interactive Experiences',
          'Location-Based Entertainment',
          'Translating emerging technologies for mainstream audiences'
        ]
      }
    ]
  },
  {
    name: 'Meet Bhanushali',
    role: 'Founding Engineer',
    initials: 'MB',
    photo: '/team/meet-bhanushali.png',
    intro:
      'Meet is a full-stack engineer specializing in XR, spatial computing, and emerging technologies. At RealityCheck, he leads platform architecture, backend systems, and product development.',
    sections: [
      {
        label: '🏢 Experience',
        items: [
          'Research with Harvard Medical School XR Lab',
          'Published author on AR, smart glasses, and other spatial technology',
          'Award winner at hackathons including MIT Reality Hack, Android XR, and Snap Specs'
        ]
      },
      {
        label: '🎯 Industry Focus',
        items: [
          'Immersive applications across XR and spatial computing',
          'Platform architecture & scalable products',
          'XR and AI hardware',
          'New platform piloting'
        ]
      }
    ]
  },
  {
    name: 'Yesha Shah',
    role: 'Founding Designer',
    initials: 'YS',
    photo: '/team/yesha-shah.png',
    intro:
      'Yesha is a product and spatial designer focused on making emerging technologies intuitive and accessible. A graduate of the Master\'s program at NYU Integrated Design Media (IDM) with a focus on XR, she leads RealityCheck\'s user experience and visual design strategy.',
    sections: [
      {
        label: '🏢 Experience',
        items: [
          'Product and spatial design for XR experiences',
          'AI prototyping and design systems',
          'Translating complex technologies into engaging consumer experiences'
        ]
      },
      {
        label: '🎯 Industry Focus',
        items: [
          'User experience & visual design',
          'XR and spatial computing interfaces',
          'Making emerging technologies accessible to mainstream audiences'
        ]
      }
    ]
  }
];

function AboutPage({
  onBack,
  onNavigateToSaved,
  onNavigateToRecommendations,
  onNavigateToCreate,
  onNavigateToMyUploads,
  onNavigateToReview,
  onNavigateToAbout,
  canCreatePost,
  canReview
}) {
  return (
    <Container>
      <TopBar>
        <BackButton type="button" onClick={onBack}>
          ← Back
        </BackButton>
        <AuthPanel
          onNavigateToSaved={onNavigateToSaved}
          onNavigateToRecommendations={onNavigateToRecommendations}
          onNavigateToCreate={onNavigateToCreate}
          onNavigateToMyUploads={onNavigateToMyUploads}
          onNavigateToReview={onNavigateToReview}
          onNavigateToAbout={onNavigateToAbout}
          canCreatePost={canCreatePost}
          canReview={canReview}
        />
      </TopBar>

      <Content>
        {/* 1. Hero */}
        <Hero>
          <HeroIntro>
            <PageTitle>About Us</PageTitle>
            <HeroSubtitle>Making Immersive Entertainment Accessible to Everyone.</HeroSubtitle>
          </HeroIntro>
          <HeroMission>
            RealityCheck exists to make immersive entertainment easier to discover, understand, and access for audiences while giving creators greater visibility and new opportunities to reach them.
          </HeroMission>
        </Hero>

        {/* 2. The Opportunity */}
        <Section>
          <SectionHeader>
            <SectionTitle>The Opportunity</SectionTitle>
            <SectionSubtitle>Closing the Discovery Gap.</SectionSubtitle>
          </SectionHeader>
          <SectionLead>
          <GapPanel>
            <GapGrid>
              <GapCard>
                <GapLabel>
                  <span aria-hidden="true">🔍</span> Audiences
                </GapLabel>
                <GapText>
                  Curious audiences have no convenient place to find immersive experiences across
                  devices, platforms, and real-world venues.
                </GapText>
              </GapCard>
              <GapConnector aria-hidden="true">
                <ConnectorArm>‹</ConnectorArm>
                <HandshakeBadge>🤝</HandshakeBadge>
                <ConnectorArm>›</ConnectorArm>
              </GapConnector>
              <GapCard>
                <GapLabel>
                  <span aria-hidden="true">🎬</span> Creators
                </GapLabel>
                <GapText>
                  Creators struggle to gain visibility and monetize their work.
                </GapText>
              </GapCard>
            </GapGrid>
            <GapBridge>RealityCheck exists to bring the two together.</GapBridge>
          </GapPanel>
          </SectionLead>
        </Section>

        {/* 3. What We Do */}
        <Section>
          <SectionHeader>
            <SectionTitle>What We Do</SectionTitle>
            <SectionSubtitle>The Complete Discovery Journey.</SectionSubtitle>
          </SectionHeader>
          <SectionLead>
          <CardGrid>
            {WHAT_WE_DO.map((item) => (
              <FeatureCard key={item.title}>
                <CardIcon aria-hidden="true">{item.icon}</CardIcon>
                <CardTitle>{item.title}</CardTitle>
                <CardText>{item.text}</CardText>
              </FeatureCard>
            ))}
          </CardGrid>
          </SectionLead>
        </Section>

        {/* 4. Our Story */}
        <Section>
          <SectionHeader>
            <SectionTitle>Our Story</SectionTitle>
            <SectionSubtitle>Built Through Research.</SectionSubtitle>
          </SectionHeader>
          <StoryIntro>
            Developed through <AndersonHighlight>UCLA Anderson School of Management</AndersonHighlight>&apos;s entrepreneurship program, where customer discovery became the foundation for every product decision.
          </StoryIntro>
          <StatsRow>
            {RESEARCH_STATS.map((stat) => (
              <StatBlock key={stat.label}>
                <StatValue>{stat.value}</StatValue>
                <StatLabel>{stat.label}</StatLabel>
              </StatBlock>
            ))}
          </StatsRow>
          <HorizontalTimeline>
            <TimelineTrack aria-hidden="true">
              {STORY_MILESTONES.map((milestone) => (
                <TimelineMarker key={milestone.title} $accent={milestone.accent} />
              ))}
            </TimelineTrack>
            <TimelineCards>
              {STORY_MILESTONES.map((milestone) => (
                <MilestoneCard key={milestone.title}>
                  <MilestoneTitle>{milestone.title}</MilestoneTitle>
                  <MilestoneText>{milestone.text}</MilestoneText>
                </MilestoneCard>
              ))}
            </TimelineCards>
          </HorizontalTimeline>
        </Section>

        {/* 5. Meet the Team */}
        <Section>
          <SectionHeader>
            <SectionTitle>Meet the Team</SectionTitle>
          </SectionHeader>
          <SectionLead>
          <TeamGrid>
            {TEAM.map((member) => (
              <TeamCard key={member.name}>
                <TeamCardHeader>
                  <Avatar aria-hidden={member.photo ? undefined : true}>
                    {member.photo ? (
                      <AvatarImage src={member.photo} alt={member.name} />
                    ) : (
                      member.initials
                    )}
                  </Avatar>
                  <MemberHeaderText>
                    <MemberName>
                      {member.name}
                      {member.pronouns && (
                        <span
                          style={{
                            fontWeight: 400,
                            color: 'rgba(255,255,255,0.55)',
                            fontSize: '0.9rem'
                          }}
                        >
                          {' '}
                          ({member.pronouns})
                        </span>
                      )}
                    </MemberName>
                    <MemberRole>{member.role}</MemberRole>
                  </MemberHeaderText>
                </TeamCardHeader>
                <MemberIntro>{member.intro}</MemberIntro>
                <BioSections>
                  {member.sections.map((section) => (
                    <BioSection key={section.label}>
                      <BioSectionTitle>{section.label}</BioSectionTitle>
                      <BioList>
                        {section.items.map((item) => {
                          const { text, indented } = normalizeBioItem(item);
                          return (
                            <BioListItem key={text} $indented={indented}>
                              {text}
                            </BioListItem>
                          );
                        })}
                      </BioList>
                    </BioSection>
                  ))}
                </BioSections>
              </TeamCard>
            ))}
          </TeamGrid>
          </SectionLead>
        </Section>
      </Content>
    </Container>
  );
}

export default AboutPage;
